import axios from 'axios'

// Environment-based API configuration
const config = {
  development: {
    baseURL: process.env.VUE_APP_API_URL || 'http://localhost:8080/api',
    timeout: 60000
  },
  production: {
    baseURL: process.env.VUE_APP_API_URL || 'https://api.testpal.com/api',
    timeout: 60000
  }
}

const env = process.env.VUE_APP_ENV || process.env.NODE_ENV || 'development'
const currentConfig = config[env] || config.development

// Storage keys (must match auth store)
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN_EXPIRES_AT: 'token_expires_at',
  USER: 'user'
}

/**
 * Clear all auth-related storage
 */
function clearAuthStorage() {
  Object.values(STORAGE_KEYS).forEach(key => {
    try {
      localStorage.removeItem(key)
    } catch (e) {
      console.warn(`Failed to remove ${key} from localStorage:`, e)
    }
  })
}

/**
 * Get router instance lazily to avoid circular dependency
 */
let routerInstance = null
function getRouter() {
  if (!routerInstance) {
    // Lazy import to avoid circular dependency
    routerInstance = require('@/router').router
  }
  return routerInstance
}

/**
 * Navigate to login page
 */
function navigateToLogin() {
  try {
    const router = getRouter()
    if (router) {
      router.push({ name: 'login' })
    } else {
      // Fallback if router not available
      window.location.href = '/'
    }
  } catch (e) {
    console.warn('Failed to navigate to login:', e)
    window.location.href = '/'
  }
}

// Create axios instance
const api = axios.create({
  baseURL: currentConfig.baseURL,
  timeout: currentConfig.timeout,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Token refresh state
let isRefreshing = false
let failedQueue = []
let refreshPromise = null

/**
 * Process queued requests after token refresh
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

/**
 * Perform token refresh
 */
async function performTokenRefresh() {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  // Use raw axios to avoid interceptors
  const response = await axios.post(
    `${currentConfig.baseURL}/v1/admin/token-refresh.json`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } }
  )

  const { access_token, refresh_token: newRefreshToken, expires_in } = response.data
  
  if (!access_token) {
    throw new Error('Invalid token refresh response')
  }

  const expiresAt = Date.now() + ((expires_in || 900) * 1000)

  // Update localStorage
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token)
  if (newRefreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken)
  }
  localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, String(expiresAt))

  // Update axios default header
  api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

  return access_token
}

/**
 * Handle authentication failure
 */
function handleAuthFailure() {
  clearAuthStorage()
  delete api.defaults.headers.common['Authorization']
  navigateToLogin()
}

// ============================================
// REQUEST INTERCEPTOR
// ============================================
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage (most up-to-date)
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    
    // Append .json to URL if not already present
    if (config.url && !config.url.endsWith('.json')) {
      // Handle URLs with query params
      const [path, query] = config.url.split('?')
      config.url = query ? `${path}.json?${query}` : `${path}.json`
    }
    
    return config
  },
  (error) => Promise.reject(error)
)

// ============================================
// RESPONSE INTERCEPTOR
// ============================================
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message)
      return Promise.reject(error)
    }

    const status = error.response.status

    // Handle 401 Unauthorized
    if (status === 401 && !originalRequest._retry) {
      // Don't retry for auth endpoints
      const isAuthEndpoint = originalRequest.url?.includes('/admin-auth') ||
                            originalRequest.url?.includes('/token-refresh')
      
      if (isAuthEndpoint) {
        handleAuthFailure()
        return Promise.reject(error)
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }).catch(err => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Use a single promise for concurrent refresh attempts
        if (!refreshPromise) {
          refreshPromise = performTokenRefresh()
        }

        const newToken = await refreshPromise
        
        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        
        // Process queued requests
        processQueue(null, newToken)
        
        // Retry the original request
        return api(originalRequest)
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        processQueue(refreshError, null)
        handleAuthFailure()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
        refreshPromise = null
      }
    }

    // Handle 403 Forbidden
    if (status === 403) {
      console.warn('Access forbidden:', error.response.data)
    }

    // Handle 500+ Server errors
    if (status >= 500) {
      console.error('Server error:', error.response.data)
    }

    return Promise.reject(error)
  }
)

// ============================================
// UTILITY METHODS
// ============================================

/**
 * Check if user is authenticated based on localStorage
 */
api.isAuthenticated = () => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
  const user = localStorage.getItem(STORAGE_KEYS.USER)
  return !!(token && user)
}

/**
 * Get current access token
 */
api.getAccessToken = () => {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
}

/**
 * Set authorization header manually
 */
api.setAuthHeader = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    delete api.defaults.headers.common['Authorization']
  }
}

/**
 * Clear auth and redirect to login
 */
api.clearAuthAndRedirect = () => {
  handleAuthFailure()
}

export default api
